import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { TimerProvider } from "@/context/TimerContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import FloatingTimer from "@/components/FloatingTimer";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import AcceptInvite from "@/pages/AcceptInvite";
import MyTasks from "@/pages/MyTasks";
import Dashboard from "@/pages/Dashboard";
import Orders from "@/pages/Orders";
import OrderDetail from "@/pages/OrderDetail";
import Clients from "@/pages/Clients";
import Automations from "@/pages/Automations";
import ClientDetail from "@/pages/ClientDetail";
import Integrations from "@/pages/Integrations";
import Calendar from "@/pages/Calendar";
import Workload from "@/pages/Workload";
import TimeTracking from "@/pages/TimeTracking";
import Music from "@/pages/Music";
import Templates from "@/pages/Templates";
import Settings from "@/pages/Settings";
import { Toaster } from "@/components/ui/sonner";

function AppRouter() {
  const location = useLocation();
  // Handle Google OAuth callback BEFORE any route/auth check to avoid race conditions.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/dashboard" element={<ProtectedRoute><Layout><RequirePage page="dashboard"><Dashboard /></RequirePage></Layout></ProtectedRoute>} />
      <Route path="/orders" element={<ProtectedRoute><Layout><RequirePage page="orders"><Orders /></RequirePage></Layout></ProtectedRoute>} />
      <Route path="/orders/:id" element={<ProtectedRoute><Layout><RequirePage page="orders"><OrderDetail /></RequirePage></Layout></ProtectedRoute>} />
      <Route path="/clients" element={<ProtectedRoute><Layout><RequirePage page="clients"><Clients /></RequirePage></Layout></ProtectedRoute>} />
      <Route path="/automations" element={<ProtectedRoute><Layout><RequirePage page="automations"><Automations /></RequirePage></Layout></ProtectedRoute>} />
      <Route path="/clients/:id" element={<ProtectedRoute><Layout><RequirePage page="clients"><ClientDetail /></RequirePage></Layout></ProtectedRoute>} />
      <Route path="/integrations" element={<ProtectedRoute><Layout><RequirePage page="integrations"><Integrations /></RequirePage></Layout></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><Layout><RequirePage page="calendar"><Calendar /></RequirePage></Layout></ProtectedRoute>} />
      <Route path="/workload" element={<ProtectedRoute><Layout><RequirePage page="workload"><Workload /></RequirePage></Layout></ProtectedRoute>} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/time-tracking" element={<ProtectedRoute><Layout><RequirePage page="time-tracking"><TimeTracking /></RequirePage></Layout></ProtectedRoute>} />
      <Route path="/music" element={<ProtectedRoute><Layout><RequirePage page="music"><Music /></RequirePage></Layout></ProtectedRoute>} />
      <Route path="/templates" element={<ProtectedRoute><Layout><RequirePage page="templates"><Templates /></RequirePage></Layout></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Layout><RequirePage page="settings"><Settings /></RequirePage></Layout></ProtectedRoute>} />
      <Route path="/my-tasks" element={<ProtectedRoute><Layout><MyTasks /></Layout></ProtectedRoute>} />
      <Route path="/" element={<HomeRedirect />} />
      <Route path="*" element={<HomeRedirect />} />
    </Routes>
  );
}

function HomeRedirect() {
  const { user } = useAuth();
  return <Navigate to={user?.is_member ? "/my-tasks" : "/dashboard"} replace />;
}

function RequirePage({ page, children }) {
  const { user } = useAuth();
  if (user?.is_member) {
    const ok = page === "my-tasks" || (page !== "integrations" && !!(user.permissions || {})[page]);
    if (!ok) return <Navigate to="/my-tasks" replace />;
  }
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <TimerProvider>
            <AppRouter />
            <FloatingTimer />
            <Toaster position="top-right" richColors />
          </TimerProvider>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
